import {
  BreakpointsEnum,
  isBreakpointDown,
  useBreakpoint,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  CommonActions,
  RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {useDispatch} from 'Provider';
import {DateTime} from 'luxon';
import {
  Box,
  Flex,
  Icon,
  Pressable,
  Text,
  View,
  useBreakpointValue,
} from 'native-base';
import React, {useEffect, useMemo, useState} from 'react';
import {LayoutChangeEvent} from 'react-native';
import FontAwesome5Pro from 'react-native-vector-icons/FontAwesome5Pro';

import {scrollViewRef} from 'components/PageTemplate';
import PaginationBar from 'components/PaginationBar';
import ConnectedOfferCard from 'containers/OfferCard';
import {
  OFFER_CARDS_MAX_CONTAINER_WIDTH,
  OFFER_GROUP_THRESHOLD,
  Offer,
} from 'models/Offer';
import {OffersActions} from 'store/offers/offers.reducer';
import {PaginatedOffers} from 'store/offers/util/paginateOffers';
import {trackEvent} from 'utils/trackEvent';

import OfferCardList from '../OfferCardList';

export enum OFFERS_GROUPING {
  CURRENT_WEEK = 'current-week',
  CURRENT_MONTH = 'current-month',
  NEXT_MONTH = 'next-month',
  FOLLOWING_NEXT_MONTH = 'following-next-month',
}

interface OffersGrouping {
  sectionTitle: string;
  seeMoreLabel: string;
  paginatedOffers: PaginatedOffers;
  key: OFFERS_GROUPING;
}

export interface GroupingMap {
  [grouping: string]: OffersGrouping;
}

/**
 * Helper function that is memoized so we are not calling now() every re-render;
 * @returns Grouping section title and labels
 */
const mapGroupings = ({
  paginatedSevenDaysOffers,
  paginatedCurrentMonthOffers,
  paginatedNextMonthOffers,
  paginatedThirdMonthOffers,
}): GroupingMap => {
  const currentMonth = DateTime.now().monthLong.toUpperCase();
  const nextMonth = DateTime.now().plus({months: 1}).monthLong.toUpperCase();
  const followingNextMonth = DateTime.now()
    .plus({months: 2})
    .monthLong.toUpperCase();

  return {
    [`${OFFERS_GROUPING.CURRENT_WEEK}`]: {
      sectionTitle: 'EXPIRING IN THE NEXT 7 DAYS',
      seeMoreLabel: 'EXPIRING IN THE NEXT 7 DAYS',
      paginatedOffers: paginatedSevenDaysOffers,
      key: OFFERS_GROUPING.CURRENT_WEEK,
    },
    [`${OFFERS_GROUPING.CURRENT_MONTH}`]: {
      sectionTitle: `${currentMonth} OFFERS`,
      seeMoreLabel: `${currentMonth} OFFERS`,
      paginatedOffers: paginatedCurrentMonthOffers,
      key: OFFERS_GROUPING.CURRENT_MONTH,
    },
    [`${OFFERS_GROUPING.NEXT_MONTH}`]: {
      sectionTitle: `${nextMonth} OFFERS`,
      seeMoreLabel: `${nextMonth} OFFERS`,
      paginatedOffers: paginatedNextMonthOffers,
      key: OFFERS_GROUPING.NEXT_MONTH,
    },
    [`${OFFERS_GROUPING.FOLLOWING_NEXT_MONTH}`]: {
      sectionTitle: `${followingNextMonth} OFFERS`,
      seeMoreLabel: `${followingNextMonth} OFFERS`,
      paginatedOffers: paginatedThirdMonthOffers,
      key: OFFERS_GROUPING.FOLLOWING_NEXT_MONTH,
    },
  };
};

export type ParamsList = {
  OffersGrouping: {group: string};
};

export interface OfferGroupProps {
  offers: Offer[];
  paginatedFilteredOffers?: PaginatedOffers;
  nextSevenDaysOffers?: Offer[];
  paginatedSevenDaysOffers?: PaginatedOffers;
  currentMonthOffers?: Offer[];
  paginatedCurrentMonthOffers?: PaginatedOffers;
  nextMonthOffers?: Offer[];
  paginatedNextMonthOffers?: PaginatedOffers;
  thirdMonthOffers?: Offer[];
  paginatedThirdMonthOffers?: PaginatedOffers;
  isDatesSelected: boolean;
  isListView: boolean;
}

export const getMarginRight = ({
  maxCardNumber,
  containerWidth,
  cardWidth,
  isTwoRowLayout,
}) => {
  containerWidth = Math.min(containerWidth, OFFER_CARDS_MAX_CONTAINER_WIDTH);
  let numCards = maxCardNumber;
  let widthRequired = cardWidth * numCards;

  if (containerWidth < widthRequired) {
    // Cannot fit cards in container, use one less card.
    numCards = numCards - 1;
    widthRequired = cardWidth * numCards;
  }

  const remainder = containerWidth - widthRequired;
  let marginRight = 4;

  if (numCards > 1) {
    // More than 1 card to show, divide remaining space into right margin
    // numCards - 1, because we don't need a margin on the last card in the row.
    marginRight = Math.max(marginRight, remainder / (numCards - 1));
  }
  if (isTwoRowLayout) {
    marginRight = 0;
  }

  if (numCards === 2) {
    marginRight = 12;
  }

  return {marginRight, numCards};
};

const OfferGroup: React.FC<OfferGroupProps> = ({
  offers,
  paginatedFilteredOffers,
  nextSevenDaysOffers,
  paginatedSevenDaysOffers,
  currentMonthOffers,
  paginatedCurrentMonthOffers,
  nextMonthOffers,
  paginatedNextMonthOffers,
  thirdMonthOffers,
  paginatedThirdMonthOffers,
  isDatesSelected,
  isListView = false,
}) => {
  const breakpoint = useBreakpoint();
  const isNano = isBreakpointDown(breakpoint, BreakpointsEnum.NANO);
  const isXsmall = isBreakpointDown(breakpoint, BreakpointsEnum.XS);
  const isDesktop = useIsDesktop();
  const isTwoRowLayout = !isNano && isXsmall;
  const listView = isNano || (isListView && !isDesktop);
  const route = useRoute<RouteProp<ParamsList, 'OffersGrouping'>>();
  const navigation = useNavigation();
  const [page, setPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const dispatch = useDispatch();

  const maxCardNumber = useBreakpointValue({
    base: 2, // 0 - 479
    sm: 3, // 480 - 767
    md: 4, // 768 - 959
    lg: 4, // 960 - 1169
    xl: 3, // 1170 - 1439
    '2xl': 4, // 1440+
  });

  const cardWidth = useBreakpointValue({
    base: 164, // 0 - 479
    sm: 176, // 480 - 767
    md: 176, // 768 - 959
    lg: 176, // 960 - 1169
    xl: 238, // 1170 - 1439
    '2xl': 238, // 1440+
  });

  const groupings = useMemo(
    () =>
      mapGroupings({
        paginatedSevenDaysOffers,
        paginatedCurrentMonthOffers,
        paginatedNextMonthOffers,
        paginatedThirdMonthOffers,
      }),
    [
      paginatedSevenDaysOffers,
      paginatedCurrentMonthOffers,
      paginatedNextMonthOffers,
      paginatedThirdMonthOffers,
    ]
  );

  const defaultGroup: string | undefined = route.params?.group;
  const [group, setGroup] = useState<OffersGrouping | undefined>(
    groupings[defaultGroup]
  );

  useEffect(() => {
    setPage(1);
    setGroup(groupings[defaultGroup]);
  }, [defaultGroup, groupings, route]);

  const handleSeeMorePressed = (selectedGroup: string) => {
    const event = {
      event: 'viewMoreOffers',
      grouping: selectedGroup,
    };

    trackEvent(event);

    navigation?.dispatch(
      CommonActions?.setParams({...route.params, group: selectedGroup})
    );
    scrollViewRef?.current?.scrollTo();
  };

  const handleBackPressed = () => {
    /**
     * Handles if user navigated directly using url and params.
     */
    if (route.params?.group) {
      return navigation?.dispatch(
        CommonActions?.setParams({...route.params, group: undefined})
      );
    }

    /**
     * Handles if we pushed from clicking 'See More'
     */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    navigation?.pop();
  };

  /**
   * Should filters be changed, we'll clear out the "See More" groupings
   * Falling back to pagination of just the filtered offers.
   */
  useEffect(() => {
    if (isDatesSelected) {
      setGroup(undefined);
      if (route.params?.group) {
        navigation?.dispatch(
          CommonActions?.setParams({...route.params, group: undefined})
        );
      }
    }
  }, [isDatesSelected, navigation, route.params]);

  const handleLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  if (
    offers.length < OFFER_GROUP_THRESHOLD ||
    (!nextSevenDaysOffers &&
      !currentMonthOffers &&
      !nextMonthOffers &&
      !thirdMonthOffers) ||
    (isDatesSelected && paginatedFilteredOffers) ||
    group
  ) {
    const {numCards, marginRight} = getMarginRight({
      cardWidth,
      containerWidth,
      maxCardNumber,
      isTwoRowLayout,
    });

    const renderItem = (item: Offer, i: number) => {
      const isLastCard = (i + 1) % numCards === 0;

      return (
        <Box
          key={`OfferCard-${item.id}`}
          mr={isLastCard || listView ? 0 : `${marginRight}px`}
          marginBottom={isDesktop ? 5 : 4}
          width={listView ? '100%' : null}
          flexBasis={isTwoRowLayout ? '48%' : undefined}>
          <ConnectedOfferCard
            setSelectedGrouping={() =>
              dispatch(
                OffersActions.setSelectedGrouping(
                  group
                    ? {key: group?.key, name: group.sectionTitle}
                    : undefined
                )
              )
            }
            cardIndex={i}
            testID="offer-card"
            offer={item}
          />
        </Box>
      );
    };

    const paginatedOffers = group?.paginatedOffers || paginatedFilteredOffers;
    const paginationByBreakpoint = paginatedOffers?.[maxCardNumber];
    const pageOfOffers = paginationByBreakpoint?.[page - 1];
    const totalPages = paginationByBreakpoint?.totalPages;

    return (
      <View alignItems="center">
        {!isDatesSelected && group && (
          <View alignItems="flex-start" width="100%">
            <View flexDirection="row" marginBottom="16px" alignItems="center">
              <Pressable
                accessibilityLabel="Go back to all offers"
                accessibilityRole="button"
                onPress={handleBackPressed}
                marginLeft={0}>
                <View
                  flexDirection="row"
                  height={36}
                  width={36}
                  borderRadius={18}
                  backgroundColor="white"
                  alignItems="center"
                  justifyContent="center"
                  shadow={0}>
                  <Icon
                    as={FontAwesome5Pro}
                    name="long-arrow-left"
                    color="paleSky"
                    size={4}
                    // @ts-expect-error Native Base icon throws a type error for the "solid" prop
                    solid
                  />
                </View>
              </Pressable>
              <Text
                fontSize={isDesktop ? 5 : '14px'}
                marginLeft={3}
                fontFamily="heading">
                {group.seeMoreLabel}
              </Text>
            </View>
          </View>
        )}
        <Flex
          onLayout={handleLayout}
          alignItems="center"
          flexDirection={listView ? 'column' : 'row'}
          wrap="wrap"
          width="100%"
          justifyContent={isTwoRowLayout ? 'space-between' : 'flex-start'}>
          {pageOfOffers && pageOfOffers.map(renderItem)}
        </Flex>
        <View alignItems="center" flex={1}>
          {totalPages && totalPages > 1 && (
            <View marginBottom={'64px'} marginTop={'48px'} width="100%">
              <PaginationBar
                isCompact={isNano}
                isFullWidth={!isDesktop}
                numPages={totalPages}
                onPageChange={setPage}
              />
            </View>
          )}
        </View>
      </View>
    );
  }

  return (
    // a negative top margin to offset the OfferCardList top padding and align elements at the top
    <View mt={isDesktop ? '-32px' : 0} onLayout={handleLayout}>
      {nextSevenDaysOffers && nextSevenDaysOffers.length > 0 && (
        <OfferCardList
          isListView={isListView}
          testIdSuffix={OFFERS_GROUPING.CURRENT_WEEK}
          sectionTitle={groupings[OFFERS_GROUPING.CURRENT_WEEK].sectionTitle}
          offers={nextSevenDaysOffers}
          handleSeeMorePressed={() => {
            handleSeeMorePressed(OFFERS_GROUPING.CURRENT_WEEK);
          }}
          setSelectedGrouping={() =>
            dispatch(
              OffersActions.setSelectedGrouping({
                key: OFFERS_GROUPING.CURRENT_WEEK,
                name: groupings[OFFERS_GROUPING.CURRENT_WEEK].sectionTitle,
              })
            )
          }
          groupingKey={OFFERS_GROUPING.CURRENT_WEEK}
        />
      )}
      {currentMonthOffers && currentMonthOffers.length > 0 && (
        <OfferCardList
          isListView={isListView}
          testIdSuffix={OFFERS_GROUPING.CURRENT_MONTH}
          sectionTitle={groupings[OFFERS_GROUPING.CURRENT_MONTH].sectionTitle}
          offers={currentMonthOffers}
          handleSeeMorePressed={() => {
            handleSeeMorePressed(OFFERS_GROUPING.CURRENT_MONTH);
          }}
          setSelectedGrouping={() =>
            dispatch(
              OffersActions.setSelectedGrouping({
                key: OFFERS_GROUPING.CURRENT_MONTH,
                name: groupings[OFFERS_GROUPING.CURRENT_MONTH].sectionTitle,
              })
            )
          }
          groupingKey={OFFERS_GROUPING.CURRENT_MONTH}
        />
      )}
      {nextMonthOffers && nextMonthOffers.length > 0 && (
        <OfferCardList
          isListView={isListView}
          testIdSuffix={OFFERS_GROUPING.NEXT_MONTH}
          sectionTitle={groupings[OFFERS_GROUPING.NEXT_MONTH].sectionTitle}
          offers={nextMonthOffers}
          handleSeeMorePressed={() => {
            handleSeeMorePressed(OFFERS_GROUPING.NEXT_MONTH);
          }}
          setSelectedGrouping={() =>
            dispatch(
              OffersActions.setSelectedGrouping({
                key: OFFERS_GROUPING.NEXT_MONTH,
                name: groupings[OFFERS_GROUPING.NEXT_MONTH].sectionTitle,
              })
            )
          }
          groupingKey={OFFERS_GROUPING.NEXT_MONTH}
        />
      )}
      {thirdMonthOffers && thirdMonthOffers.length > 0 && (
        <OfferCardList
          isListView={isListView}
          testIdSuffix={OFFERS_GROUPING.FOLLOWING_NEXT_MONTH}
          sectionTitle={
            groupings[OFFERS_GROUPING.FOLLOWING_NEXT_MONTH].sectionTitle
          }
          offers={thirdMonthOffers}
          handleSeeMorePressed={() => {
            handleSeeMorePressed(OFFERS_GROUPING.FOLLOWING_NEXT_MONTH);
          }}
          setSelectedGrouping={() =>
            dispatch(
              OffersActions.setSelectedGrouping({
                key: OFFERS_GROUPING.FOLLOWING_NEXT_MONTH,
                name: groupings[OFFERS_GROUPING.FOLLOWING_NEXT_MONTH]
                  .sectionTitle,
              })
            )
          }
          groupingKey={OFFERS_GROUPING.FOLLOWING_NEXT_MONTH}
        />
      )}
    </View>
  );
};

export default OfferGroup;
